import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { skillsService } from '../services';
import type { CreateSkillDto, UpdateSkillDto } from '../types';

const SKILLS_KEY = 'skills';

/**
 * Hook to fetch all skills
 */
export function useSkills(category?: string) {
  return useQuery({
    queryKey: [SKILLS_KEY, 'list', category],
    queryFn: () => skillsService.list(category),
  });
}

/**
 * Hook to fetch a single skill
 */
export function useSkill(id: number | undefined) {
  return useQuery({
    queryKey: [SKILLS_KEY, 'detail', id],
    queryFn: () => skillsService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to search skills
 */
export function useSkillSearch(query: string, limit: number = 20) {
  return useQuery({
    queryKey: [SKILLS_KEY, 'search', query, limit],
    queryFn: () => skillsService.search(query, limit),
    enabled: query.length >= 2,
  });
}

/**
 * Hook to get skill categories
 */
export function useSkillCategories() {
  return useQuery({
    queryKey: [SKILLS_KEY, 'categories'],
    queryFn: () => skillsService.getCategories(),
  });
}

/**
 * Hook to create a skill
 */
export function useCreateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateSkillDto) => skillsService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SKILLS_KEY] });
    },
  });
}

/**
 * Hook to update a skill
 */
export function useUpdateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateSkillDto }) => skillsService.update(id, dto),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [SKILLS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SKILLS_KEY, 'detail', id] });
    },
  });
}

/**
 * Hook to delete a skill
 */
export function useDeleteSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => skillsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SKILLS_KEY] });
    },
  });
}
